import { FC, ReactNode } from "react"
import Head from "next/head"
import Image, { getImageProps } from "next/image"
import classNames from "classnames"

import { readImage } from "../../app/utils/read-image"
import { TRUSTPILOT_REVIEWS_URL, GOOGLE_MAPS_URL } from "../../constants/brand"
import { Anchor } from "../../app/components/anchor"
import { useReviewsContext } from "../../context/ReviewsContext/ReviewsContextProvider"
import {
  PREMIUM_HERO_FORM_COMPACT_LAYOUT,
  PremiumHeroForm,
} from "../../components/premium/hero-form"
import StarRating from "../../components/StarRating"

import BgPaws from "../../assets/images/bg-paws.png"
import { BlokImage } from ".."
import PageNav, { NavigationType } from "./PageNav"
import LocalRatingCard from "@/components/LocalRatingCard"
import Breadcrumb, { BreadcrumbItem } from "@/components/breadcrumbs"

const PETS_HERO_BG_ALT = "pets related page"
const DESKTOP_PETS_HERO_MEDIA = "(min-width: 1024px)"
const desktopPetsHeroImage = getImageProps({
  src: BgPaws,
  fill: true,
  sizes: "100vw",
  alt: PETS_HERO_BG_ALT,
})

export interface ILocalPetsHero {
  blok: {
    tagline: string
    title: string
    description: string
    buttonLink?: string
    buttonLabel?: string
    image?: BlokImage
    smImage?: BlokImage
    navItems?: NavigationType["navItems"]
    categories?: NavigationType["categories"]
    isNavHidden?: boolean
    ctaRoute?: string
    hasNavigation?: boolean
    showBadge?: boolean
    hasBookingGuarantee?: number
    hasSocialProof?: number
    isAnimals?: boolean
  }
  items: BreadcrumbItem[]
  shipmentProof?: ReactNode
  subHeading?: string
}

const LocalPetsHero: FC<ILocalPetsHero> = ({
  subHeading,
  blok,
  items,
  shipmentProof,
}) => {
  const {
    tagline,
    title,
    description,
    buttonLabel,
    buttonLink,
    image,
    ctaRoute,
    navItems = [],
    categories = [],
    isNavHidden,
    hasNavigation,
    hasBookingGuarantee,
    hasSocialProof = false,
    smImage,
    isAnimals,
  } = blok
  const { blokReviews } = useReviewsContext()

  return (
    <>
      {hasNavigation && (
        <PageNav
          noBlok
          route={ctaRoute || buttonLink}
          buttonLabel={buttonLabel}
          navigationData={{ navItems, categories, isNavHidden }}
          position={""}
        />
      )}
      <section
        id="pets-hero"
        className={classNames("relative bg-secondary-100 mt-20 pt-8", {
          "lg:h-[820px]": hasSocialProof,
          "lg:h-[780px]": !hasSocialProof,
        })}
      >
        <div>
          {isAnimals && (
            <>
              <Head>
                <link
                  key="pets-hero-bg-paws-preload"
                  rel="preload"
                  as="image"
                  href={
                    desktopPetsHeroImage.props.srcSet
                      ? undefined
                      : desktopPetsHeroImage.props.src
                  }
                  imageSrcSet={desktopPetsHeroImage.props.srcSet}
                  imageSizes={desktopPetsHeroImage.props.sizes}
                  media={DESKTOP_PETS_HERO_MEDIA}
                />
              </Head>
              <Image
                src={BgPaws}
                fill
                sizes="100vw"
                alt={PETS_HERO_BG_ALT}
                className="z-0 hidden lg:block object-contain lg:-mt-4 object-center"
              />
            </>
          )}

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 lg:max-lg:h-[200px] lg:h-[624px] relative max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto lg:p-4 lg:pt-10 lg:px-8 lg:max-lg:px-4 pb-2 lg:pb-1">
            <div className="text-black8 font-merriweather w-full lg:w-1/2 lg:max-w-[651px] text-left px-6 pt-1 lg:pt-4">
              {hasSocialProof && (
                <div
                  className="flex font-work_sans mb-6 lg:-mt-10"
                  id="reviews"
                >
                  <section className="w-[128px] h-[60px] p-2 rounded-lg shadow-md bg-white mr-2">
                    <Image
                      className="h-[22px] w-auto"
                      src="/icons/google.svg"
                      alt="google logo"
                      width={100}
                      height={20}
                    />

                    <div className="w-full flex flex-row justify-start items-center text-sm">
                      <StarRating value={5} className="w-20" />

                      <Anchor
                        href={GOOGLE_MAPS_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        dofollow
                        passHref
                      >
                        {blokReviews?.googleRatings}
                      </Anchor>
                    </div>
                  </section>
                  <section className="w-[128px] h-[60px] p-2 rounded-lg shadow-md bg-white">
                    <Image
                      className="h-[22px] w-auto"
                      src="/icons/trust-pilot.svg"
                      alt="trust pilot logo"
                      width={100}
                      height={20}
                    />

                    <div className="w-full flex flex-row  justify-start items-center text-sm">
                      <StarRating value={5} className="w-20" />

                      <Anchor
                        href={TRUSTPILOT_REVIEWS_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        dofollow
                        passHref
                      >
                        {blokReviews?.trustpilotRatings}
                      </Anchor>
                    </div>
                  </section>
                </div>
              )}
              <div>
                {" "}
                <Breadcrumb
                  items={items}
                  className="font-inter lg:-mt-4 mb-6 text-[14px]"
                />
              </div>
              {tagline && (
                <p className="text-md font-light italic mt-2.5 lg:mt-2 mb-0">
                  {tagline}
                </p>
              )}
              <h1 className="text-[32px] leading-[63px] lg:leading-[60px] lg:text-[38px] xl:leading-[68px] xl:text-[40px] font-bold mt-1 lg:mt-4 mb-3">
                {title}
              </h1>
              {subHeading && (
                <p className="text-lg font-inter mb-3 font-semibold">
                  {subHeading}
                </p>
              )}
              <p className="text-lg mb-0 leading-7 sm:leading-8 lg:leading-9 lg:max-w-lg font-inter text-black8 text-opacity-70">
                {description}
              </p>
            </div>

            <div className="w-full lg:w-1/2 lg:flex items-center justify-center p-0 z-20">
              <div className="flex flex-col justify-center items-center">
                {image && (
                  <Image
                    src={readImage(image)}
                    alt={image.alt}
                    width={472}
                    height={418}
                    className={classNames(
                      "w-[405px] h-[358px] lg:w-[472px] lg:h-[418px] mb-12 lg:mb-0 object-cover object-center",
                      smImage?.filename ? "hidden lg:block" : "block",
                    )}
                  />
                )}
                <section className="mt-3.5 hidden lg:block">
                  <LocalRatingCard />
                </section>
              </div>

              {smImage?.filename && (
                <>
                  <Image
                    src={readImage(smImage)}
                    alt={smImage.alt}
                    width={375}
                    height={324}
                    className={classNames(
                      "w-full min-h-[324px] sm:w-[400px] text-center  mx-auto mb-2 lg:hidden order-first lg:object-cover aspect-ratio object-center",
                    )}
                  />
                </>
              )}
            </div>
          </div>
          <div className="relative lg:absolute lg:-bottom-24 max-w-7xl mx-auto  bg-white rounded-1 lg:h-[218px] px-6 md:px-5 lg:px-10 py-4 lg:py-11 right-0 left-0 lg:border border-gray52 shadow-sm">
            <div className="lg:flex justify-center sm:max-w-md  lg:max-w-none  md:max-w-auto sm:mx-auto">
              {" "}
              <PremiumHeroForm
                buttonLabel={buttonLabel}
                buttonLink={buttonLink}
                customClassName={PREMIUM_HERO_FORM_COMPACT_LAYOUT}
              />
              <section className="mt-4 mb-7 mx-4 sm:mx-0 lg:hidden">
                <LocalRatingCard />
              </section>
            </div>
            {isAnimals && (
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 pt-4 pb-2 text-sm text-gray-600 font-inter">
                {shipmentProof}
                <span className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4 text-yellow-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {blokReviews?.googleRatings ?? "4.8"}/5 from{" "}
                  {Number(
                    (blokReviews?.totalReviews ?? "48600").replace(/,/g, ""),
                  ).toLocaleString("en-US")}{" "}
                  reviews
                </span>
                <span className="hidden sm:inline text-gray-300">|</span>
                <span className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  $1K Pet Protection
                </span>
                <span className="hidden sm:inline text-gray-300">|</span>
                <span className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Background-Checked Drivers
                </span>
              </div>
            )}
            {hasBookingGuarantee && (
              <small className="pt-4 flex items-center sm:mr-10 justify-start">
                {" "}
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 50 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    opacity="0.1"
                    width="50"
                    height="50"
                    rx="25"
                    fill="#1B53E1"
                  />
                  <path
                    d="M32.63 15.8067L26.2133 13.4034C25.5483 13.1584 24.4633 13.1584 23.7983 13.4034L17.3817 15.8067C16.145 16.2734 15.1417 17.72 15.1417 19.0384V28.4884C15.1417 29.4334 15.76 30.6817 16.5183 31.2417L22.935 36.0367C24.0667 36.8884 25.9217 36.8884 27.0533 36.0367L33.47 31.2417C34.2283 30.67 34.8467 29.4334 34.8467 28.4884V19.0384C34.8583 17.72 33.855 16.2734 32.63 15.8067ZM29.06 22.34L24.0433 27.3567C23.8683 27.5317 23.6467 27.6134 23.425 27.6134C23.2033 27.6134 22.9817 27.5317 22.8067 27.3567L20.94 25.4667C20.6017 25.1284 20.6017 24.5684 20.94 24.23C21.2783 23.8917 21.8383 23.8917 22.1767 24.23L23.4367 25.49L27.835 21.0917C28.1733 20.7534 28.7333 20.7534 29.0717 21.0917C29.41 21.43 29.41 22.0017 29.06 22.34Z"
                    fill="#9F6D25"
                  />
                </svg>
                <span className="inline-block pl-3 text-sm">
                  All transports are covered under our{" "}
                  <span className="text-gold">
                    <Anchor
                      href="https://help.citizenshipper.com/hc/en-us/articles/360041203251-CitizenShipper-Booking-Assurance-Guarantee"
                      className="text-gold hover:underline"
                      newTab
                    >
                      Booking Assurance Guarantee.
                    </Anchor>
                  </span>
                </span>
              </small>
            )}
          </div>
        </div>
      </section>
      <section className="lg:mb-10 lg:py-12 lg:mb-12" />
    </>
  )
}

export default LocalPetsHero
